import { PromotionInstance } from '@atlassian/commerce-ccp-gateway-client';
import { CartLinkedEntitlementsSchema, CartProductSchema, ProductStatus } from '../../types';
import type { AppPromotionResponse, State } from '../types';
import { getSelectedAppsObj, getSelectedProductsAppsObj } from '.';
import { CartPromotionAction } from '../../ui/product-line-items/src/types';

/**
 * this function is used to add or remove promotion from selected product or app
 * @param identifier is a unique identifier for product or app
 * @param promotion is the promotion object which is to be added
 * @param promotionId is the promotion id which is to be removed
 * @param promotionAction is the action to be performed on promotion
 * @param isApp is a boolean to check if the product is app or not
 * @param state is the current state of the store
 */
export function getUpdatedEntitlementWithPromotions({
  identifier,
  promotion,
  promotionId,
  promotionAction,
  isApp,
  state,
}: {
  identifier: string;
  promotion?: AppPromotionResponse;
  promotionId?: string;
  promotionAction: CartPromotionAction;
  isApp?: boolean;
  state: State;
}) {
  if (isApp) {
    const selectedLinkedEntitlements = { ...state.selectedLinkedEntitlements };
    // getting the selected app with given identifier
    const selectedApp = selectedLinkedEntitlements?.[identifier];

    // selecting the app with given identifier
    const appDetail = Object.values(state?.linkedEntitlements)
      ?.flatMap((item) => item)
      ?.find((app) => app.id === identifier);
    const isNewApp = appDetail?.productStatuses?.includes(ProductStatus.NEW);
    let selectedAppWithPromotions: Record<string, Partial<CartLinkedEntitlementsSchema>> = {};

    if (promotionAction === CartPromotionAction.ADD_APP_PROMOTION) {
      /*
       * Adding new promotion to the particular selected app in store
       */
      if (promotion) {
        const appliedPromotions = [...(selectedApp?.promotions ?? []), promotion];
        const productStatusesObj = isNewApp ? {} : { productStatuses: [ProductStatus.EDITED] };
        selectedAppWithPromotions = {
          [identifier]: { ...selectedApp, promotions: appliedPromotions, ...productStatusesObj },
        };
        return {
          selectedLinkedEntitlements: { ...selectedLinkedEntitlements, ...selectedAppWithPromotions },
        };
      }
    }

    if (promotionAction === CartPromotionAction.REMOVE_APP_PROMOTION) {
      /*
       * Deleting specific promotion from the particular selected app in store
       */
      const promotions = selectedApp?.promotions?.filter((promotion) => promotion?.id !== promotionId);

      // checking if promotion is already applied and that promotion is to remove
      const removeExistingPromotions = (appDetail?.promotions ?? [])
        ?.map((promotion) => {
          if (promotion?.id === promotionId) {
            return { promotionInstanceId: promotion?.promotionInstanceId };
          }
        })
        .filter((item) => !!item);

      // merging all promotions which are ready to remove
      const removePromotions = [
        ...(selectedApp?.removePromotions ?? []),
        ...removeExistingPromotions,
      ] as PromotionInstance[];

      // while removing promotions from selectedLinkedEntitlements check if other app updates applicable like nextBillDate
      const stitchedAppsObj = getSelectedAppsObj(state) ?? {};
      const updatedApps = getSelectedProductsAppsObj({
        initialProducts: stitchedAppsObj,
        selectedProducts: selectedLinkedEntitlements,
        identifier: identifier,
        changeObj: {
          ...(promotions?.length ? { promotions } : {}),
          ...(removePromotions?.length ? { removePromotions } : {}),
        },
        ignoreKeys: ['productStatuses'],
      });
      selectedAppWithPromotions = updatedApps as Record<string, Partial<CartLinkedEntitlementsSchema>>;
      // if appliedPromotions is empty we need to remove the promotions property from selected app payload
      if (promotions?.length === 0) {
        delete selectedAppWithPromotions?.[identifier].promotions;
      }
      return {
        selectedLinkedEntitlements: { ...selectedLinkedEntitlements, ...selectedAppWithPromotions },
      };
    }
  }

  if (promotionAction === CartPromotionAction.REMOVE_PRODUCT_PROMOTION) {
    const product = state.products?.[identifier];

    // getting the selected product with given identifier
    const selectedProduct = state.selectedProducts?.[identifier];
    // checking if promotion is already applied and that promotion is to remove
    const appliedPromotions = product?.promotions?.find((promotion) => promotion?.id === promotionId);
    const removeExistingPromotions = appliedPromotions?.promotionInstanceId
      ? { promotionInstanceId: appliedPromotions?.promotionInstanceId }
      : {};

    const productStatuses = [...(selectedProduct?.productStatuses ?? [])];

    // merging all promotions which are ready to remove
    const removePromotions = [...(selectedProduct?.removePromotions ?? [])];
    if (removeExistingPromotions?.promotionInstanceId) {
      removePromotions.push(removeExistingPromotions);
      if (!selectedProduct.productStatuses?.includes(ProductStatus.EDITED)) {
        productStatuses.push(ProductStatus.EDITED);
      }
    }

    // while removing promotions from selectedProducts check if other product updates applicable
    const updatedProducts: Record<string, Partial<CartProductSchema>> = getSelectedProductsAppsObj({
      initialProducts: state.products,
      selectedProducts: state.selectedProducts,
      identifier,
      changeObj: {
        productStatuses: productStatuses,
        ...(removePromotions?.length ? { removePromotions } : {}),
      },
      ignoreKeys: ['productStatuses'],
    });

    return {
      selectedProducts: { ...updatedProducts },
    };
  }
}
